import type {
  ArchitectInvite,
  CpaInvite,
  CRMUser,
  EmployeeInvite,
  EngineerInvite,
  SupplierInvite,
} from '../types';
import type { ContractorApplicant } from '../types/contractorOnboard';
import { contractorFullName, normalizeContractorEmail } from './contractorOnboard';

export function isUnrosteredContractorApplicant(
  applicant: Pick<ContractorApplicant, 'email' | 'emailNormalized' | 'contractorUserId'>,
  crmUsers: Array<Pick<CRMUser, 'uid' | 'email' | 'is1099Contractor'>>,
): boolean {
  if (applicant.contractorUserId?.trim()) {
    const uid = applicant.contractorUserId.trim();
    if (crmUsers.some(u => u.uid === uid)) return false;
  }
  const email = normalizeContractorEmail(applicant.emailNormalized || applicant.email || '');
  if (email) {
    const match = crmUsers.find(u => normalizeContractorEmail(u.email || '') === email);
    // Same email as a W-2 staff login does not hide a separate crew applicant (e.g. test data).
    if (match?.is1099Contractor === true) return false;
  }
  return true;
}

/** @deprecated Prefer {@link isUnrosteredContractorApplicant}. */
export function isLooseContractorApplicant(
  applicant: Pick<ContractorApplicant, 'email' | 'emailNormalized' | 'contractorUserId'>,
  crmUsers: Array<Pick<CRMUser, 'uid' | 'email' | 'is1099Contractor'>>,
): boolean {
  return isUnrosteredContractorApplicant(applicant, crmUsers);
}

function filterApplicantSearch(applicant: ContractorApplicant, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  const name = contractorFullName(applicant).toLowerCase();
  const email = (applicant.email || '').toLowerCase();
  return name.includes(q) || email.includes(q);
}

export function filterLooseContractorApplicants(
  applicants: ContractorApplicant[],
  crmUsers: Array<Pick<CRMUser, 'uid' | 'email' | 'is1099Contractor'>>,
  search = '',
): ContractorApplicant[] {
  return applicants
    .filter(a => a.status !== 'pooled')
    .filter(a => isUnrosteredContractorApplicant(a, crmUsers))
    .filter(a => filterApplicantSearch(a, search))
    .sort((a, b) => contractorFullName(a).localeCompare(contractorFullName(b)));
}

export function filterPooledContractorApplicants(
  applicants: ContractorApplicant[],
  crmUsers: Array<Pick<CRMUser, 'uid' | 'email' | 'is1099Contractor'>>,
  search = '',
): ContractorApplicant[] {
  return applicants
    .filter(a => a.status === 'pooled')
    .filter(a => isUnrosteredContractorApplicant(a, crmUsers))
    .filter(a => filterApplicantSearch(a, search))
    .sort((a, b) => contractorFullName(a).localeCompare(contractorFullName(b)));
}

export type TeamPendingInviteKind =
  | 'cpa'
  | 'employee'
  | 'architect'
  | 'engineer'
  | 'supplier';

export type TeamPendingInvite = {
  id: string;
  kind: TeamPendingInviteKind;
  name: string;
  email: string;
  label: string;
};

function pendingStatus(status: string | undefined): boolean {
  return (status || '').toLowerCase() === 'pending';
}

function emailTaken(email: string, crmUsers: Array<Pick<CRMUser, 'email'>>): boolean {
  const n = email.trim().toLowerCase();
  if (!n) return false;
  return crmUsers.some(u => (u.email || '').trim().toLowerCase() === n);
}

export function buildTeamPendingInvites(input: {
  crmUsers: Array<Pick<CRMUser, 'email'>>;
  cpaInvites: CpaInvite[];
  employeeInvites: EmployeeInvite[];
  architectInvites: ArchitectInvite[];
  engineerInvites: EngineerInvite[];
  supplierInvites: SupplierInvite[];
  search?: string;
}): TeamPendingInvite[] {
  const { crmUsers } = input;
  const q = (input.search || '').trim().toLowerCase();
  const rows: TeamPendingInvite[] = [];

  for (const inv of input.cpaInvites) {
    if (!inv.id || !pendingStatus(inv.status) || emailTaken(inv.email, crmUsers)) continue;
    rows.push({
      id: inv.id,
      kind: 'cpa',
      name: inv.name,
      email: inv.email,
      label: 'CPA',
    });
  }
  for (const inv of input.employeeInvites) {
    if (!inv.id || !pendingStatus(inv.status) || emailTaken(inv.email, crmUsers)) continue;
    rows.push({
      id: inv.id,
      kind: 'employee',
      name: inv.name,
      email: inv.email,
      label: inv.isWorkstation ? 'Workstation' : inv.role || 'Employee',
    });
  }
  for (const inv of input.architectInvites) {
    if (!inv.id || !pendingStatus(inv.status) || emailTaken(inv.email, crmUsers)) continue;
    const kindLabel = inv.inviteKind === 'architect' ? 'Architect' : 'Designer';
    rows.push({
      id: inv.id,
      kind: 'architect',
      name: inv.name,
      email: inv.email,
      label: kindLabel,
    });
  }
  for (const inv of input.engineerInvites) {
    if (!inv.id || !pendingStatus(inv.status) || emailTaken(inv.email, crmUsers)) continue;
    rows.push({
      id: inv.id,
      kind: 'engineer',
      name: inv.name,
      email: inv.email,
      label: 'Engineer',
    });
  }
  for (const inv of input.supplierInvites) {
    if (!inv.id || !pendingStatus(inv.status) || emailTaken(inv.email, crmUsers)) continue;
    rows.push({
      id: inv.id,
      kind: 'supplier',
      name: inv.name,
      email: inv.email,
      label: 'Supplier',
    });
  }

  return rows
    .filter(r => {
      if (!q) return true;
      return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.label.toLowerCase().includes(q);
    })
    .sort((a, b) => a.name.localeCompare(b.name) || a.email.localeCompare(b.email));
}

export function looseContractorHiringHref(applicant: Pick<ContractorApplicant, 'id' | 'lastJobId'>): string {
  const params = new URLSearchParams();
  params.set('tab', 'crew');
  if (applicant.lastJobId) params.set('job', applicant.lastJobId);
  params.set('applicant', applicant.id);
  return `/people/hiring?${params.toString()}`;
}
